import React, { useState } from 'react';
import { PublicHeader } from '../components/PublicHeader';
import { motion } from 'framer-motion';
import { Play, Brain, Grid3x3, Video, Trophy, Users, Target, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuthDialog from '../components/AuthDialog';

export default function LandingPage() {
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const features = [
    {
      icon: Video,
      title: 'Vídeos Educacionais',
      description: 'Conteúdo exclusivo do canal do Beto Carvalho com explicações dinâmicas',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Brain,
      title: 'Quiz Interativo',
      description: 'Teste seus conhecimentos com questões de múltiplos níveis',
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: Grid3x3,
      title: 'Caça-Palavras',
      description: 'Aprenda brincando com puzzles temáticos',
      color: 'from-orange-500 to-red-500'
    },
    {
      icon: Trophy,
      title: 'Sistema de Pontuação',
      description: 'Acompanhe seu progresso e compete com outros',
      color: 'from-green-500 to-emerald-500'
    }
  ];

  const stats = [
    { icon: Users, value: '10K+', label: 'Alunos' },
    { icon: Video, value: '50+', label: 'Vídeos' },
    { icon: Target, value: '1000+', label: 'Questões' },
  ];

  const handleCTA = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      setShowAuthDialog(true);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />

      <section className="pt-24 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary mb-6">
              <Sparkles className="h-4 w-4" />
              <span className="text-sm font-medium">Apaixonado por Conhecimentos Gerais</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Aprenda com
              <span className="block bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                Beto Carvalho
              </span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Plataforma educacional gamificada com vídeos, quiz e jogos para você dominar conhecimentos gerais
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                onClick={handleCTA}
                data-testid="landing-cta-primary"
                size="lg"
                className="h-14 px-8 text-lg bg-gradient-to-r from-primary to-secondary"
              >
                <Play className="h-5 w-5 mr-2" />
                Começar Agora
              </Button>
              <Button
                onClick={() => navigate('/sobre')}
                data-testid="landing-cta-secondary"
                size="lg"
                variant="outline"
                className="h-14 px-8 text-lg"
              >
                Saber Mais
              </Button>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-20">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6 rounded-2xl bg-white border border-border"
              >
                <stat.icon className="h-8 w-8 mx-auto mb-3 text-primary" />
                <p className="text-3xl font-bold mb-1">{stat.value}</p>
                <p className="text-muted-foreground">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              O que você vai encontrar
            </h2>
            <p className="text-xl text-muted-foreground">
              Recursos completos para seu aprendizado
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="p-8 rounded-2xl bg-white border border-border hover:shadow-lg transition-all"
              >
                <div className={`inline-flex h-14 w-14 rounded-xl bg-gradient-to-br ${feature.color} items-center justify-center mb-4`}>
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-3">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-4xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Pronto para começar?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Cadastre-se gratuitamente e comece a aprender hoje mesmo
            </p>
            <Button
              onClick={handleCTA}
              data-testid="landing-cta-bottom"
              size="lg"
              className="h-14 px-8 text-lg bg-gradient-to-r from-primary to-secondary"
            >
              Criar Conta Grátis
            </Button>
          </motion.div>
        </div>
      </section>

      <footer className="border-t border-border py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Beto Carvalho</h3>
                <p className="text-xs text-muted-foreground">Apaixonado por Conhecimentos Gerais</p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <a href="https://www.instagram.com/beto_carvalho_ofc/" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">
                Instagram
              </a>
              <a href="https://www.youtube.com/@beto_carvalho_ofc" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">
                YouTube
              </a>
              <a href="https://www.tiktok.com/@beto_carvalho_ofc" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">
                TikTok
              </a>
            </div>
          </div>
          <div className="mt-8 text-center text-sm text-muted-foreground">
            © 2025 Beto Carvalho. Todos os direitos reservados.
          </div>
        </div>
      </footer>

      <AuthDialog open={showAuthDialog} onClose={() => setShowAuthDialog(false)} />
    </div>
  );
}
